import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, TableRow, Tooltip, type SxProps, type Theme } from '@mui/material';
import { GripVerticalIcon } from 'react-line-awesome';

/**
 * The handle lives in the gutter the field table already reserves at its left edge
 * (`fieldTableStyles` indents the first cell), so a row that can be dragged keeps exactly
 * the columns of a locked inherited one and the two tables stay aligned.
 */
const rowHandleStyles: SxProps<Theme> = {
  position: 'absolute',
  left: 18,
  top: '50%',
  transform: 'translateY(-50%)',
};

const handleStyles: SxProps<Theme> = {
  display: 'inline-flex',
  alignItems: 'center',
  color: 'text.disabled',
  fontSize: 16,
  cursor: 'grab',
  // Without this the browser claims the gesture as a scroll and the pointer sensor never
  // sees the drag on a touch device.
  touchAction: 'none',
  '&:hover': { color: 'text.secondary' },
  '&:active': { cursor: 'grabbing' },
};

interface SortableProps {
  /** Identifies the entry inside its own list; see `PoiTypeSchema` for the encoding. */
  id: string;
  /** Nothing to reorder, or nothing the reader may reorder: renders without a handle. */
  disabled: boolean;
  handleTitle: string;
  children: (handle: React.ReactNode) => React.ReactNode;
}

/** One draggable field row. The handle goes inside the first cell — a `tr` holds only cells. */
export const SortableRow: React.FC<SortableProps> = ({ id, disabled, handleTitle, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });

  return (
    <TableRow
      hover
      ref={setNodeRef}
      sx={{
        position: 'relative',
        transform: CSS.Transform.toString(transform),
        transition,
        ...(isDragging && { opacity: 0.4 }),
      }}
    >
      {children(disabled ? null : (
        <Tooltip title={handleTitle} arrow>
          <Box component="span" sx={{ ...handleStyles, ...rowHandleStyles }} {...attributes} {...listeners}>
            <GripVerticalIcon />
          </Box>
        </Tooltip>
      ))}
    </TableRow>
  );
};

/** One draggable block — a group, handle placed by the caller inside its header. */
export const SortableBlock: React.FC<SortableProps & { sx?: SxProps<Theme> }> = ({
  id, disabled, handleTitle, sx, children,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });

  return (
    <Box
      ref={setNodeRef}
      sx={{
        ...sx,
        position: 'relative',
        transform: CSS.Transform.toString(transform),
        transition,
        ...(isDragging && { opacity: 0.4, zIndex: 1 }),
      }}
    >
      {children(disabled ? null : (
        <Tooltip title={handleTitle} arrow>
          <Box component="span" sx={handleStyles} {...attributes} {...listeners}>
            <GripVerticalIcon />
          </Box>
        </Tooltip>
      ))}
    </Box>
  );
};
